/**
 * (owner's daughter, an idea report: "moro iskä saaks täs olla esim poliisi" — can I be a police officer here; the owner, Sep 25:
 * "go and build it") The police shift.
 *
 * A work job (`content/jobs/police_shift.json`, kind work / `police`), offered by Hustl near a police station and at the station's
 * counter; not to anyone with wanted stars. Report to the station: the cruiser is waiting outside; get in and you are on duty —
 * the uniform goes on (the job's `uniform` cosmetics, over your own look until the shift ends; everyone sees it, and the
 * cruiser's lights). Dispatch sends calls, one at a time, from the job's `dispatch` list (kind, weight, pay, the radio line):
 *
 *  - **speeder**: a car doing twice the limit. Get behind it with the siren on (H) until it stops, then walk up to it: ticket.
 *  - **chase**: a car that runs. Stay on it until it wrecks, or box it in until the driver gives up.
 *  - **burglar**: an alarm at a house or a shop; the burglar runs when you come. Catch them on foot (or knock them down).
 *  - **crash**: two cars in a smash. Get out at the scene and hold it (six seconds) until the tow is on its way.
 *
 * Each call pays when it is done; a call not done in time goes to another unit. The shift pays its base on top at the end
 * (J, "End the shift") — "Good work, officer!". A crime in uniform (any wanted star) ends it on the spot: no bonus, karma and
 * reputation lost. Hitting the suspect's car on a chase is the job, not a crime (game.ts damageVehicle asks `onTheCall`).
 *
 * The suspects' cars are driven here, as a race's rivals are in jobs.ts (`ai.chase`: the town's traffic loop leaves them to us):
 * a speeder towards a point far ahead, a runner away from you — `driveNpc` with a goal, no new driving code.
 */
import { count as countAch } from './achievements';
import type { Game, PlayerState } from './game';
import type { ActiveJob } from './jobs';
import type { Look } from './cosmetics';
import { makePerson, makeVehicle, forward, type Person, type VehicleEntity } from './entities';
import { driveNpc, laneCentre } from './ai';
import { tileBlocked, updateVehicle, vehicleBlocked } from './physics';
import { RF_SIDEWALK } from '../world/generator';
import { doorstep } from './property';
import { karma } from './animals';
import { vehicleMaxHp, type ShopRow } from './shop';
import { accept } from './jobs';
import type { PlacedBuilding } from '../world/buildings';

export type CallKind = 'speeder' | 'chase' | 'burglar' | 'crash';
export interface DispatchDef { kind: CallKind; weight: number; pay: number; say: string }
export interface Call {
  kind: CallKind; pay: number; /** seconds on it */ t: number; place: string;
  x: number; y: number;
  /** the suspect's car (speeder, chase), the burglar, the crash's cars */
  vid?: number; pid?: number; vids?: number[];
  /** speeder: 'run' → 'stopped' (siren behind it) → ticket; chase: 'run' → done; burglar: 'wait' → 'run'; crash: holding the scene */
  phase: 'run' | 'stopped' | 'wait'; hold: number; goal?: { x: number; y: number }; seen?: boolean; retarget?: number;
}
export interface PoliceShift { on: boolean; /** the last call's kind: not the same twice running when there is a choice */ lastKind?: CallKind; call?: Call; /** seconds to the next call */ next: number; calls: number; earned: number; /** the look under the uniform */ look?: Look; /** crash cars to clear once you are gone */ clear: { vid: number; at: number }[] }

/** a call not done in this many seconds goes to another unit */
export const CALL_TIME = 150;
/** seconds between calls, and before the first */
export const CALL_GAP = 8, FIRST_CALL = 4;
/** the speeder: siren behind it within this many metres for this long and it pulls over; the ticket at this reach on foot */
export const PULL = { reach: 18, hold: 1.2, ticket: 3.5 };
/** the chase: the driver gives up when boxed in (slow, you close) this long; a car below this share of its hp is done */
export const BOX = { reach: 14, hold: 3, wreck: 0.3 };
/** the burglar runs from this far; caught at this reach on foot */
export const CATCH = { spook: 24, reach: 1.8 };
/** the crash: hold the scene on foot within this many metres for this long */
export const SCENE = { reach: 9, hold: 6 };

const shiftOf = (st: PlayerState): PoliceShift | undefined => st.job?.police;
/** the cruiser the shift issued (the work state's vehicle) */
function cruiserOf(game: Game, j: ActiveJob): VehicleEntity | undefined { const vid = j.work?.vid; return vid === undefined ? undefined : game.vehicles.find((v) => v.id === vid); }
const say = (game: Game, st: PlayerState, text: string) => game.say(text, st.person);
const dispatch = (game: Game, st: PlayerState, text: string) => game.say(`📻 Dispatch: ${text}`, st.person);

/** Is this vehicle the suspect of the player's call? (their hitting it is police work, not a crime) */
export function onTheCall(game: Game, byId: number, v: VehicleEntity): boolean { const st = game.players.get(byId); const c = st ? shiftOf(st)?.call : undefined; return !!c && (c.vid === v.id); }

/** The shift's first moment in the cruiser: the uniform on, dispatch says hello. */
export function startDuty(game: Game, st: PlayerState, j: ActiveJob): void {
  const def = game.reg.get('job', j.id); const p = st.person;
  const s: PoliceShift = (j.police ??= { on: false, next: FIRST_CALL, calls: 0, earned: 0, clear: [] });
  if (s.on) return;
  s.on = true; s.look = p.look ? { ...p.look } : undefined;
  const look: Look = { ...(p.look ?? {}) };
  for (const id of def?.uniform ?? []) { const c = game.reg.get('cosmetic', id); if (c && (c.slot === 'top' || c.slot === 'hat' || c.slot === 'glasses')) look[c.slot] = c.id; }
  p.look = look;
  say(game, st, '👮 On duty, officer! The uniform is on. Siren: H (on a phone, the Car app). Dispatch will call you.');
}

/** Per step of the shift once on duty (growth.ts stepWork hands it here). */
export function stepPoliceShift(game: Game, st: PlayerState, j: ActiveJob, dt: number): void {
  const p = st.person, s = j.police;
  if (!s?.on) { startDuty(game, st, j); return; }
  if (st.wanted > 0) { offDuty(game, st, 'crime'); return; }
  const car = cruiserOf(game, j);
  if (car?.wrecked) { say(game, st, 'Your cruiser is a wreck — the shift is over'); endPoliceShift(game, st, false); return; }
  for (let i = s.clear.length - 1; i >= 0; i--) { const c = s.clear[i]; if (game.time < c.at) continue; const v = game.vehicles.find((x) => x.id === c.vid); if (v && !v.driver) { v.home = 'lost'; s.clear.splice(i, 1); } else if (!v) s.clear.splice(i, 1); }
  if (!s.call) {
    s.next -= dt;
    if (s.next <= 0) { if (!startCall(game, st, j)) s.next = 3; }
    return;
  }
  const c = s.call; c.t += dt;
  if (c.t > CALL_TIME) { dispatch(game, st, 'Another unit has that one — stand by.'); releaseCall(game, s); s.next = CALL_GAP; return; }
  // (a car the world unloaded, far from everybody, is a call lost — see game.ts unloadFar: a 'duty' car is kept within reach)
  const done = c.kind === 'speeder' ? stepSpeeder(game, st, c, dt) : c.kind === 'chase' ? stepChase(game, st, c, dt) : c.kind === 'burglar' ? stepBurglar(game, st, c, dt) : stepCrash(game, st, c, dt);
  if (done === 'lost') { dispatch(game, st, c.kind === 'burglar' ? 'The suspect got away — stand by.' : 'We lost them — stand by.'); releaseCall(game, s); s.next = CALL_GAP; return; }
  if (done) {
    const pay = c.pay; p.items.cash = (p.items.cash ?? 0) + pay; s.earned += pay; s.calls++; if (j.work) { j.work.units = s.calls; j.work.earned = s.earned; }
    game.events.push({ kind: 'cash', x: p.x, y: p.y, cls: 'in' });
    say(game, st, `${done} +$${pay}`);
    countAch(game, st, 'police', 1); countAch(game, st, `police_${c.kind}`, 1); if (c.kind === 'burglar') countAch(game, st, 'arrests', 1); countAch(game, st, 'earned', pay); // (owner, Sep 28: a call counts as it closes — the Siren Shooter opens at ten)
    releaseCall(game, s, true); s.next = CALL_GAP;
  }
}

/** a place's name for the radio: the building there, else the town, else the zone */
function placeName(game: Game, x: number, y: number): string {
  let best: string | undefined, bd = 40;
  for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) { if (b.height <= 0 || b.ruined) continue; const d = Math.hypot(b.x + b.w / 2 - x, b.y + b.h / 2 - y); if (d < bd) { bd = d; best = b.def.name; } }
  const town = game.world.gen.fields.settlements.in(x, y)?.name;
  return best ? `${best}${town ? `, ${town}` : ''}` : town ?? 'the road out of town';
}
function pickKind(game: Game, list: DispatchDef[], avoid?: CallKind): DispatchDef | undefined {
  const pool = list.filter((d) => d.kind !== avoid && d.weight > 0); if (!pool.length) return list[0];
  let r = game.npcRng.float() * pool.reduce((a, d) => a + d.weight, 0);
  for (const d of pool) { r -= d.weight; if (r <= 0) return d; }
  return pool[pool.length - 1];
}
/** Dispatch sends the next call: a kind by weight (not the same twice running when there is a choice), set up near the player. */
export function startCall(game: Game, st: PlayerState, j: ActiveJob, force?: CallKind): boolean {
  const def = game.reg.get('job', j.id); const list = (def?.dispatch ?? []) as DispatchDef[]; if (!list.length) return false;
  const s = j.police!; const last = s.lastKind;
  const tries: DispatchDef[] = force ? list.filter((d) => d.kind === force) : [];
  if (!force) { const first = pickKind(game, list, last); if (first) tries.push(first); for (const d of list) if (!tries.includes(d)) tries.push(d); }
  for (const d of tries) { const c = setUp(game, st, d); if (c) { s.call = c; s.lastKind = d.kind; dispatch(game, st, d.say.replace('{place}', c.place)); return true; } }
  return false;
}
function setUp(game: Game, st: PlayerState, d: DispatchDef): Call | undefined {
  const p = st.person, rng = game.npcRng;
  const base = { kind: d.kind, pay: d.pay, t: 0, phase: 'run' as const, hold: 0 };
  if (d.kind === 'speeder' || d.kind === 'chase') {
    const v = roadCar(game, p.x, p.y, 60, 140); if (!v) return undefined; // (within the 160 m the world keeps a car near somebody)
    v.home = 'duty'; v.ai!.chase = true; v.ai!.ignorePeds = 0;
    const [fx, fy] = forward(v.angle);
    return { ...base, vid: v.id, x: v.x, y: v.y, place: placeName(game, v.x, v.y), goal: d.kind === 'speeder' ? { x: v.x + fx * 400, y: v.y + fy * 400 } : { x: v.x + fx * 300, y: v.y + fy * 300 }, retarget: 0 };
  }
  if (d.kind === 'burglar') {
    const tags = ['house', 'residential', 'shop', 'store', 'apartment'];
    const cand: { x: number; y: number; name: string }[] = [];
    for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) { if (b.height <= 0 || b.ruined || !b.def.tags.some((t) => tags.includes(t))) continue; const dd = Math.hypot(b.x + b.w / 2 - p.x, b.y + b.h / 2 - p.y); if (dd < 50 || dd > 140) continue; /* (the world keeps people 160 m from somebody) */ const door = doorstep(b, game.seed); cand.push({ x: door.x, y: door.y, name: b.def.name }); }
    const at = rng.pick(cand); if (!at) return undefined;
    const q = makePerson(at.x, at.y); q.role = 'burglar'; q.walker = { dx: 0, dy: 0, think: 0, idle: 1e6 }; q.hp = q.maxHp = 60;
    game.people.push(q);
    const town = game.world.gen.fields.settlements.in(at.x, at.y)?.name;
    return { ...base, phase: 'wait', pid: q.id, x: at.x, y: at.y, place: `${at.name}${town ? `, ${town}` : ''}` };
  }
  // the crash: two cars nose to nose on a road, smoking
  const a = roadCar(game, p.x, p.y, 60, 150, true); if (!a) return undefined;
  const [fx, fy] = forward(a.angle);
  const b = roadCarAt(game, a.x + fx * 3.6, a.y + fy * 3.6, a.angle + Math.PI * (0.6 + rng.float() * 0.5));
  for (const v of [a, b]) { if (!v) continue; v.ai = undefined; v.home = 'duty'; v.hp = Math.max(1, vehicleMaxHp(v) * 0.22); v.bumped = 0.3; v.speed = 0; }
  return { ...base, vids: [a, b].filter((v): v is VehicleEntity => !!v).map((v) => v.id), x: a.x + fx * 1.8, y: a.y + fy * 1.8, place: placeName(game, a.x, a.y) };
}
/** a civilian car put on a road in its lane, `rMin`–`rMax` from a point (a race's rival is born the same way) */
function roadCar(game: Game, cx: number, cy: number, rMin: number, rMax: number, parked = false): VehicleEntity | undefined {
  const w = game.world, rng = game.npcRng;
  for (let tries = 0; tries < 60; tries++) {
    const a = rng.float() * Math.PI * 2, r = rMin + rng.float() * (rMax - rMin);
    let x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    if (!w.roadAt(x, y) || w.roadFlags(x, y) & RF_SIDEWALK) continue;
    const hit = w.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.road.class !== 'rail'); if (!hit) continue;
    const def = civilianCar(game); if (!def) return undefined;
    const axis = hit.axis, dir: 1 | -1 = rng.float() < 0.5 ? 1 : -1;
    const lane = laneCentre(hit, axis, dir, false, def.size[0]); if (axis === 'v') x = lane; else y = lane;
    const angle = axis === 'v' ? (dir === -1 ? 0 : Math.PI) : dir === 1 ? Math.PI / 2 : -Math.PI / 2;
    const v = makeVehicle(def, x, y, angle, 'duty');
    if (vehicleBlocked(w, v, x, y, angle, 'drive') || game.vehicles.some((o) => Math.hypot(o.x - x, o.y - y) < 8)) continue;
    { const [fx, fy] = forward(angle); let clear = true; for (let k = 2; k <= 6; k++) if (!w.roadAt(x + fx * k, y + fy * k) || tileBlocked(w, x + fx * k, y + fy * k, 0, 'drive')) { clear = false; break; } if (!clear) continue; }
    v.fuel = 1;
    if (!parked) v.ai = { axis, dir, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0 };
    game.vehicles.push(v);
    return v;
  }
  return undefined;
}
function roadCarAt(game: Game, x: number, y: number, angle: number): VehicleEntity | undefined {
  const def = civilianCar(game); if (!def) return undefined;
  const v = makeVehicle(def, x, y, angle, 'duty'); if (vehicleBlocked(game.world, v, x, y, angle, 'drive')) return undefined;
  game.vehicles.push(v); return v;
}
function civilianCar(game: Game) { const defs = game.reg.all('vehicle').filter((v) => (v.class === 'car' || v.class === 'sports') && !v.tags.includes('police') && !v.tags.includes('emergency') && !v.tags.includes('military') && !v.weapons?.length); return defs.length ? game.npcRng.pick(defs) : undefined; }

function suspectCar(game: Game, c: Call): VehicleEntity | undefined { return c.vid === undefined ? undefined : game.vehicles.find((v) => v.id === c.vid); }
/** drive the suspect's car towards its goal (it keeps its `ai.chase`: the traffic loop leaves it to us) */
function drive(game: Game, v: VehicleEntity, goal: { x: number; y: number }, dt: number): boolean { if (!v.ai) return false; v.ai.ignorePeds = Math.max(v.ai.ignorePeds ?? 0, 0); return driveNpc(game.world, v, game.vehicles, game.people, game.npcRng, dt, goal); }
/** the player's car right behind this one with its siren going */
function sirenBehind(v: VehicleEntity, p: Person): boolean {
  const pc = p.vehicle; if (!pc || !pc.siren || pc.driver !== p) return false;
  const dx = pc.x - v.x, dy = pc.y - v.y, d = Math.hypot(dx, dy); if (d > PULL.reach) return false;
  const [fx, fy] = forward(v.angle); return dx * fx + dy * fy < 2; // (behind it, or beside: not out in front)
}

function stepSpeeder(game: Game, st: PlayerState, c: Call, dt: number): string | false | 'lost' {
  const p = st.person, v = suspectCar(game, c); if (!v || v.wrecked) return v?.wrecked ? '💥 The speeder crashed — the ambulance has them. Call closed.' : 'lost';
  c.x = v.x; c.y = v.y;
  if (Math.hypot(v.x - p.x, v.y - p.y) > 420) return 'lost';
  if (c.phase === 'run') {
    if (Math.hypot(v.x - c.goal!.x, v.y - c.goal!.y) < 50) { const [fx, fy] = forward(v.angle); c.goal = { x: v.x + fx * 400, y: v.y + fy * 400 }; }
    if (!drive(game, v, c.goal!, dt)) v.ai = { axis: 'v', dir: 1, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0, chase: true }; // (parked itself: back on the road)
    if (sirenBehind(v, p)) { c.hold += dt; if (c.hold >= PULL.hold) { c.phase = 'stopped'; c.hold = 0; say(game, st, '🚨 They are pulling over! Stop behind them, get out (E) and walk up to the car'); } }
    else c.hold = Math.max(0, c.hold - dt * 0.5);
    return false;
  }
  // pulled over: it brakes and waits for the ticket
  updateVehicle(game.world, v, { throttle: v.speed > 0.3 ? -1 : v.speed < -0.3 ? 1 : 0, steer: 0, up: false, down: false }, dt);
  if (!p.vehicle && Math.hypot(v.x - p.x, v.y - p.y) < PULL.ticket + v.def.size[1] / 2) return '📝 Ticket written — "Slow down, please." Call closed.';
  return false;
}
function stepChase(game: Game, st: PlayerState, c: Call, dt: number): string | false | 'lost' {
  const p = st.person, v = suspectCar(game, c); if (!v) return 'lost';
  c.x = v.x; c.y = v.y;
  const d = Math.hypot(v.x - p.x, v.y - p.y);
  if (v.wrecked || v.hp <= vehicleMaxHp(v) * BOX.wreck) return '💥 The getaway car is done — suspect in cuffs! Call closed.';
  if (d > 420) return 'lost';
  if (!c.seen && d < 60) { c.seen = true; say(game, st, '🚨 They have seen you — they are running! Stay on them: ram them or box them in'); }
  // away from the officer: the goal is re-aimed every few seconds, three hundred metres on from the car away from you
  c.retarget = (c.retarget ?? 0) - dt;
  if (c.seen && c.retarget! <= 0) { c.retarget = 3; const ux = (v.x - p.x) / (d || 1), uy = (v.y - p.y) / (d || 1); c.goal = { x: v.x + ux * 300, y: v.y + uy * 300 }; }
  if (!c.seen && Math.hypot(v.x - c.goal!.x, v.y - c.goal!.y) < 50) { const [fx, fy] = forward(v.angle); c.goal = { x: v.x + fx * 300, y: v.y + fy * 300 }; }
  if (!drive(game, v, c.goal!, dt)) { v.ai = { axis: 'v', dir: 1, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0, chase: true }; }
  if (d < BOX.reach && Math.abs(v.speed) < 1.5) { c.hold += dt; if (c.hold >= BOX.hold) return '🙌 Boxed in — the driver gives up. Suspect in cuffs! Call closed.'; }
  else c.hold = Math.max(0, c.hold - dt);
  return false;
}
function stepBurglar(game: Game, st: PlayerState, c: Call, dt: number): string | false | 'lost' {
  const p = st.person, q = game.people.find((x) => x.id === c.pid); if (!q) return 'lost';
  c.x = q.x; c.y = q.y;
  const d = Math.hypot(q.x - p.x, q.y - p.y);
  if (q.dead) return 'lost'; // (somebody else got there first)
  if (q.hp < (q.maxHp ?? 100) * 0.6) { if (d > 30) q.hp = q.maxHp ?? 60; /* (a guard or a car far from you: not your arrest — up and running again) */ else { q.walker = { dx: 0, dy: 0, think: 0, idle: 1e6 }; return '👮 Down and cuffed! The burglar is on the way to the station. Call closed.'; } }
  if (!p.vehicle && d < CATCH.reach) return '👮 Gotcha! Cuffed — the burglar is on the way to the station. Call closed.';
  if (d > 380) return 'lost';
  if (c.phase === 'wait' && d < CATCH.spook) { c.phase = 'run'; say(game, st, '🏃 The burglar is running! Catch them on foot'); }
  if (c.phase === 'run' && q.walker) {
    const ux = (q.x - p.x) / (d || 1), uy = (q.y - p.y) / (d || 1);
    if (!(q.walker.flee! > 0.5)) { q.walker.flee = 2; q.walker.fx = ux; q.walker.fy = uy; }
  }
  void dt;
  return false;
}
function stepCrash(game: Game, st: PlayerState, c: Call, dt: number): string | false | 'lost' {
  const p = st.person;
  const d = Math.hypot(c.x - p.x, c.y - p.y);
  if (d > 500) return 'lost';
  if (!p.vehicle && d < SCENE.reach) {
    const before = Math.floor(c.hold); c.hold += dt;
    if (Math.floor(c.hold) !== before && c.hold < SCENE.hold) say(game, st, `🚧 Holding the scene… ${Math.ceil(SCENE.hold - c.hold)}`);
    if (c.hold >= SCENE.hold) return '🚧 Scene secure — nobody hurt, the tow trucks are on the way. Call closed.';
  }
  return false;
}
/** the call is over: the suspect's car back to the traffic (or left where it stopped), the burglar taken away, the crash cleared later */
function releaseCall(game: Game, s: PoliceShift, done = false): void {
  const c = s.call; s.call = undefined; if (!c) return;
  const v = c.vid !== undefined ? game.vehicles.find((x) => x.id === c.vid) : undefined;
  if (v) { v.home = 'lost'; if (v.ai) { if (c.kind === 'speeder' && !v.wrecked) { v.ai.chase = false; v.ai.wait = done ? 2 : 0; } else v.ai = undefined; } } // (back to the traffic, gone once nobody is near)
  if (c.pid !== undefined) { const i = game.people.findIndex((x) => x.id === c.pid); if (i >= 0) game.people.splice(i, 1); }
  for (const vid of c.vids ?? []) s.clear.push({ vid, at: game.time + 20 });
}

/** The shift ends at J ("End the shift") or when the time is up: the bonus when there was at least one call, "Good work, officer!" */
export function endPoliceShift(game: Game, st: PlayerState, bonus = true): void {
  const j = st.job, s = j?.police, p = st.person; if (!j) return;
  const def = game.reg.get('job', j.id);
  if (s) releaseCall(game, s);
  const pay = bonus && s && s.calls > 0 ? Math.round(j.pay || (def?.pay.base ?? 0)) : 0; // (the offer's pay: doubled when the introduction brought it)
  if (pay) { p.items.cash = (p.items.cash ?? 0) + pay; game.events.push({ kind: 'cash', x: p.x, y: p.y, cls: 'in' }); }
  offUniform(game, st, j);
  if (s && s.calls > 0) say(game, st, `👮 Good work, officer! ${s.calls} call${s.calls === 1 ? '' : 's'}, $${s.earned}${pay ? ` + $${pay} for the shift` : ''}. See you tomorrow.`);
  else say(game, st, 'Shift over — no calls closed this time. See you tomorrow, officer.');
  if (s && s.calls > 0) { st.jobsDone = (st.jobsDone ?? 0) + 1; countAch(game, st, 'jobs', 1); countAch(game, st, 'jobs_work', 1); game.markIntroduced(st, j.id); }
  st.job = undefined; st.jobsOpen = false;
}
/** A crime in uniform: off duty at once — no bonus, karma and a step of reputation gone. */
export function offDuty(game: Game, st: PlayerState, why: 'crime'): void {
  const j = st.job, s = j?.police; if (!j) return;
  if (s) releaseCall(game, s);
  offUniform(game, st, j);
  karma(game, st, -2, 'an officer broke the law in uniform');
  st.jobsDone = Math.max(0, (st.jobsDone ?? 0) - 1);
  say(game, st, `🚫 Off duty — an officer does not break the law. The shift is over: no bonus, and the city trusts you a little less.`); void why;
  st.job = undefined; st.jobsOpen = false;
}
/** the uniform off (the look from before), the cruiser left for the station to fetch */
export function offUniform(game: Game, st: PlayerState, j: ActiveJob): void {
  const s = j.police, p = st.person;
  if (s?.on) { p.look = s.look ? { ...s.look } : undefined; s.on = false; }
  const car = cruiserOf(game, j);
  if (car) { car.siren = false; if (!car.driver && !car.wrecked && Math.hypot(car.x - p.x, car.y - p.y) > 25) game.vehicles.splice(game.vehicles.indexOf(car), 1); else car.home = 'lost'; }
}

/** (Sep 25) the police station's counter: the shift, for anyone not wanted and not on another job (the client draws the same rows) */
export function policeRows(reg: { get(kind: 'job', id: string): { name: string; pay: { base: number }; dispatch?: { pay: number }[] } | undefined }, busy: boolean): ShopRow[] {
  const def = reg.get('job', SHIFT_ID); if (!def || busy) return [];
  const pays = (def.dispatch ?? []).map((c) => c.pay);
  return [{ kind: 'lesson', id: 'police:shift', name: `👮 Start a police shift — $${Math.min(...pays)}–$${Math.max(...pays)} a call, $${def.pay.base} at the end`, price: 0 }];
}
/** the shift taken at the counter: the job as Hustl would offer it, from this station */
export function startShiftAt(game: Game, st: PlayerState, b: PlacedBuilding): void {
  const def = game.reg.get('job', SHIFT_ID); if (!def) return;
  if (st.job) { game.say('Finish the job you have first', st.person); return; }
  const door = doorstep(b, game.seed);
  accept(game, st, { id: def.id, text: def.text.replace('{from}', b.def.name).replace('{to}', b.def.name), pay: def.pay.base, from: { x: door.x, y: door.y, name: b.def.name, key: `${b.x},${b.y}` }, to: { x: door.x, y: door.y, name: b.def.name, key: `${b.x},${b.y}` }, dist: 0 });
}
/** the job the counter hands out */
export const SHIFT_ID = 'police_shift';

/** What the HUD points at: the call, or the station while waiting. */
export function policeObjective(game: Game, j: ActiveJob, p: { x: number; y: number }): { x: number; y: number; label: string; ring?: number } {
  const s = j.police, c = s?.call;
  if (!c) return { x: p.x, y: p.y, label: `👮 On patrol — waiting for dispatch · ${s?.calls ?? 0} call${s?.calls === 1 ? '' : 's'}, $${s?.earned ?? 0}` };
  if (c.kind === 'speeder') return { x: c.x, y: c.y, label: c.phase === 'stopped' ? '📝 Walk up to the car — the ticket' : '🚨 Speeder — get behind it, siren on (H)', ring: c.phase === 'stopped' ? PULL.ticket + 2 : undefined };
  if (c.kind === 'chase') return { x: c.x, y: c.y, label: '🚨 Getaway car — ram it or box it in' };
  if (c.kind === 'burglar') return { x: c.x, y: c.y, label: c.phase === 'wait' ? `🔔 Alarm — ${c.place}` : '🏃 Catch the burglar on foot', ring: 2 };
  return { x: c.x, y: c.y, label: c.hold > 0 ? `🚧 Holding the scene… ${Math.max(0, Math.ceil(SCENE.hold - c.hold))}` : '🚧 Crash — get out at the scene', ring: SCENE.reach };
}

export type { Person };
